import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminAction } from "@/lib/adminApi";
import {
  AdminPageHeader,
  AdminStatRibbon,
  AdminPanel,
  AdminPageSkeleton,
  AdminEmptyState,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";

interface Subscriber {
  id: string;
  email: string;
  source: string;
  status: string;
  subscribed_at: string;
  unsubscribed_at: string | null;
  created_at: string;
}

function statusTone(s: string): "success" | "neutral" | "danger" {
  if (s === "subscribed") return "success";
  if (s === "suppressed") return "danger";
  return "neutral";
}

export function AdminNewsletterTab() {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "newsletter_subscribers", page, statusFilter],
    queryFn: () =>
      adminAction("newsletter_subscribers", {
        page,
        status_filter: statusFilter || undefined,
      }),
  });

  const rows: Subscriber[] = data?.rows || [];
  const total = data?.total || 0;
  const totalActive = data?.total_active ?? 0;
  const totalUnsubscribed = data?.total_unsubscribed ?? 0;
  const totalPages = Math.ceil(total / 50);

  const handleExportCSV = async () => {
    const res = await adminAction("export_active_newsletter");
    const exportRows = (res?.rows ?? []) as Array<{ email: string; subscribed_at: string; status: string }>;
    const csvHeader = "Email,Signup Date,Status\n";
    const csvBody = exportRows
      .map((r) => `"${r.email}","${new Date(r.subscribed_at).toISOString().split("T")[0]}","${r.status}"`)
      .join("\n");
    const blob = new Blob([csvHeader + csvBody], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sorvaine-newsletter-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <AdminPageSkeleton stats={3} rows={6} columns={4} />;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Marketing"
        title="Newsletter"
        description="Manage subscribers, monitor list health, and export mailing lists."
        meta={`${total.toLocaleString()} ${total === 1 ? "subscriber" : "subscribers"}`}
        actions={
          <Button variant="outline" size="sm" onClick={handleExportCSV} className="h-9 text-[13px]">
            <Download className="w-3.5 h-3.5 mr-2" /> Export active
          </Button>
        }
      />

      <AdminStatRibbon
        items={[
          { label: "Total", value: total },
          { label: "Active", value: totalActive },
          { label: "Unsubscribed", value: totalUnsubscribed },
        ]}
      />

      <div className="flex gap-3 items-center">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(0); }}>
          <SelectTrigger className="w-56 h-9 text-[13px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="subscribed">Subscribed</SelectItem>
            <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
            <SelectItem value="suppressed">Suppressed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {rows.length === 0 ? (
        <AdminEmptyState icon={Mail} title="No subscribers" description="Once people sign up, they'll appear here." />
      ) : (
        <>
          <AdminPanel>
            <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
                <tr>
                  <th className={adminTableHeadCellClass}>Email</th>
                  <th className={adminTableHeadCellClass}>Source</th>
                  <th className={adminTableHeadCellClass}>Status</th>
                  <th className={adminTableHeadCellClass}>Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className={adminTableRowClass} style={{ borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)" }}>
                    <td className={adminTableCellClass + " text-foreground"}>{r.email}</td>
                    <td className={adminTableCellClass + " text-muted-foreground"}>{r.source}</td>
                    <td className={adminTableCellClass}><AdminPill tone={statusTone(r.status)}>{r.status}</AdminPill></td>
                    <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                      {new Date(r.subscribed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminPanel>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} className="text-[13px] h-8">Previous</Button>
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Page {page + 1} of {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} className="text-[13px] h-8">Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
